import { createSignal, For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type {
    IcpColumnField,
    IcpCompanyRowScope,
    IcpGroupRowScope,
    IcpPersonaCellScope,
    IcpPersonaTableScope,
    IcpScope
} from 'mormaii-web-presentation/icp'

import { ChoiceFilter } from '../../../components/filters'
import { Icon } from '../../../components/icons'
import { DataTable, Pager } from '../../../components/table'
import { Tabs } from '../../../components/tabs'
import { MetricCard, Panel, Spinner } from '../../../components/ui'

import Css from './icp.module.scss'

/**
 * O Perfil de Cliente Ideal.
 *
 * Os filtros, a aba, a ordem e a pagina do scorecard estao todos no ENDERECO: um recorte
 * desta tela e um link que se manda a alguem. O scope entrega o texto pronto de cada
 * celula; a view so desenha.
 */
export function IcpView(props: ViewProps<IcpScope>): JSX.Element {
    return (
        <div class={Css.screen} aria-busy={props.scope.loading}>
            <FilterBar scope={props.scope} />

            <Tabs
                items={props.scope.tabs.map((tab) => ({
                    key: tab.value,
                    label: tab.label,
                    active: tab.current,
                    onSelect: () => void tab.onSelect()
                }))}
            />

            <Show when={props.scope.error}>
                {(message) => (
                    <p role="alert" class={Css.error}>
                        {message()}
                    </p>
                )}
            </Show>

            <Show when={props.scope.loading && !props.scope.hasOverview}>
                <div class={Css.computing}>
                    <Spinner class={Css.spinner} />
                    Apurando o ICP das empresas…
                </div>
            </Show>

            <Show when={props.scope.tab === 'companies' && props.scope.hasOverview}>
                <Companies scope={props.scope} />
            </Show>

            <Show when={props.scope.tab === 'persona'}>
                <Persona scope={props.scope} />
            </Show>
        </div>
    )
}

// ========== A BARRA DE FILTROS ==========

function FilterBar(props: { scope: IcpScope }): JSX.Element {
    return (
        <section class={Css.filterBar}>
            <div class={Css.haloTop} />
            <div class={Css.haloBottom} />

            <div class={Css.filterBarBody}>
                <div class={Css.header}>
                    <h2 class={Css.filterBarTitle}>Filtrar empresas</h2>

                    <button
                        type="button"
                        disabled={props.scope.loading}
                        onClick={() => void props.scope.onRefresh()}
                        class={Css.glassButton}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class={Css.icon}
                        >
                            <Show when={props.scope.loading} fallback={<path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />}>
                                <path d="M21 12a9 9 0 1 1-9-9" class={Css.spinningIcon} />
                            </Show>
                        </svg>
                        Atualizar
                    </button>
                </div>

                <div class={Css.filterBarFields}>
                    <div class={Css.axis}>
                        <span class={Css.axisLabel}>Janela</span>
                        <div class={Css.toggle}>
                            <For each={props.scope.windows}>
                                {(option) => (
                                    <button
                                        type="button"
                                        onClick={() => void option.onSelect()}
                                        class={Css.side}
                                        classList={{ [Css.current]: option.current }}
                                    >
                                        {option.label}
                                    </button>
                                )}
                            </For>
                        </div>
                    </div>

                    <div class={Css.choice}>
                        <ChoiceFilter
                            onDark
                            label="Segmento"
                            summary={props.scope.segmentSummary}
                            placeholder="Todos os segmentos"
                            hasValue={!!props.scope.segmentSummary}
                            onClear={() => void props.scope.onClearSegment()}
                            options={props.scope.segments.map((segment) => ({
                                label: segment.label,
                                value: segment.value,
                                current: segment.current,
                                onSelect: segment.onSelect
                            }))}
                        />
                    </div>

                    <div class={Css.choice}>
                        <ChoiceFilter
                            onDark
                            label="Porte"
                            summary={props.scope.sizeSummary}
                            placeholder="Todos os portes"
                            hasValue={!!props.scope.sizeSummary}
                            onClear={() => void props.scope.onClearSize()}
                            options={props.scope.sizes.map((size) => ({
                                label: size.label,
                                value: size.value,
                                current: size.current,
                                onSelect: size.onSelect
                            }))}
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

// ========== EMPRESAS ==========

function Companies(props: { scope: IcpScope }): JSX.Element {
    return (
        <div class={Css.group}>
            <div class={Css.cards}>
                <For each={props.scope.kpis}>
                    {(kpi) => (
                        // O `title` explica de onde sai o numero - o cartao nao tem espaco para isso.
                        <div title={kpi.help}>
                            <MetricCard icon={kpi.icon} label={kpi.label} value={kpi.value} hint={kpi.hint} />
                        </div>
                    )}
                </For>
            </div>

            <IdealProfile scope={props.scope} />
            <Scorecard scope={props.scope} />
            <Comparison scope={props.scope} />
            <Methodology scope={props.scope} />
        </div>
    )
}

function IdealProfile(props: { scope: IcpScope }): JSX.Element {
    return (
        <Panel title="Perfil ideal hoje">
            <p class={Css.note}>{props.scope.idealSummary}</p>

            <Show
                when={props.scope.idealEmpty}
                fallback={
                    <ul class={Css.highlights}>
                        <For each={props.scope.highlights}>
                            {(highlight) => (
                                <li class={Css.highlight}>
                                    <span class={Css.highlightDimension}>{highlight.dimension}: </span>
                                    <span class={Css.highlightValue}>{highlight.value}</span>
                                    <span class={Css.highlightDetail}>{highlight.detail}</span>
                                </li>
                            )}
                        </For>
                    </ul>
                }
            >
                {(empty) => <p class={Css.empty}>{empty()}</p>}
            </Show>

            <Show when={props.scope.smallSample}>
                <p class={Css.caution}>
                    <Icon name="alert" size={16} />
                    Amostra pequena: são poucas empresas com score, então o perfil é uma pista, não uma conclusão. Os
                    números são contagens, sem teste estatístico.
                </p>
            </Show>
        </Panel>
    )
}

function Scorecard(props: { scope: IcpScope }): JSX.Element {
    return (
        <Show when={props.scope.hasScorecard}>
            <Panel
                title="Scorecard por empresa"
                actions={
                    <Show when={props.scope.total > 0}>
                        <button type="button" onClick={() => void props.scope.onExportScorecard()} class={Css.export}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.75"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                class={Css.icon}
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                            </svg>
                            Exportar CSV
                        </button>
                    </Show>
                }
                flush
            >
                <div class={Css.scorecardTop}>
                    <p class={Css.note}>{props.scope.scorecardDescription}</p>

                    {/*
                        O atalho para classificar: empresa sem segmento fica fora do perfil por
                        segmento, e quem classifica precisa achar quais sao.
                    */}
                    <Show when={props.scope.unsegmentedCount > 0}>
                        <button
                            type="button"
                            onClick={() => void props.scope.onToggleUnsegmented()}
                            class={Css.shortcut}
                            classList={{ [Css.current]: props.scope.onlyUnsegmented }}
                        >
                            {props.scope.onlyUnsegmented
                                ? 'Mostrar todas'
                                : `Só ${props.scope.unsegmentedCount === 1 ? 'a' : 'as'} ${props.scope.unsegmentedCount} sem segmento`}
                        </button>
                    </Show>

                    <Show when={props.scope.onlyUnsegmented}>
                        <p class={Css.note}>
                            Classifique o segmento e os colaboradores contratados em Empresas, na edição de cada uma.
                        </p>
                    </Show>
                </div>

                <DataTable
                    columns={props.scope.columns.map((column) => ({
                        field: column.field,
                        label: column.label,
                        sortable: true,
                        sorted: column.sorted,
                        descending: column.descending,
                        onSort: column.onSort,
                        movable: column.movable,
                        onDrop: (source: string) => column.onDrop(source)
                    }))}
                    rows={props.scope.rows}
                    loading={props.scope.loading}
                    cell={(row, field) => companyCell(row, field as IcpColumnField)}
                    layout={props.scope.layout}
                    emptyText="Nenhuma empresa ativa neste recorte."
                />

                <Pager
                    page={props.scope.page}
                    pageCount={props.scope.pageCount}
                    pages={props.scope.pageWindow}
                    total={props.scope.total}
                    onPrevious={() => void props.scope.onPreviousPage()}
                    onNext={() => void props.scope.onNextPage()}
                    onGoTo={(page) => void props.scope.onGoToPage(page)}
                />

                <p class={Css.footnote}>
                    * Porte pelos colaboradores cadastrados, porque a empresa ainda não tem o número contratado.
                </p>
            </Panel>
        </Show>
    )
}

/** As celulas de uma linha, na ORDEM das colunas do presenter. */
/** A celula de uma coluna, pelo campo dela - a ordem das colunas e a que a pessoa escolheu. */
function companyCell(row: IcpCompanyRowScope, field: IcpColumnField): JSX.Element {
    switch (field) {
        case 'name':
            return (
                <button
                    type="button"
                    onClick={() => void row.onOpenCollaborators()}
                    title="Ver os colaboradores desta empresa"
                    class={Css.companyLink}
                >
                    {row.name}
                </button>
            )
        case 'score':
            return (
                <span class={Css.score} classList={{ [Css.muted]: row.scoreMuted }}>
                    {row.scoreLabel}
                </span>
            )
        case 'band':
            return <span class={`${Css.band} ${BAND_CLASSES[row.band] ?? ''}`}>{row.bandLabel}</span>
        case 'segment':
            return (
                <Show when={row.segmentLabel} fallback={<span class={Css.muted}>Sem segmento</span>}>
                    {row.segmentLabel}
                </Show>
            )
        case 'size':
            return (
                <Show when={row.sizeLabel} fallback={<span class={Css.muted}>-</span>}>
                    <span title={row.sizeHint}>
                        {row.sizeLabel}
                        <Show when={row.sizeEstimated}>
                            <span class={Css.muted}> *</span>
                        </Show>
                    </span>
                </Show>
            )
        case 'collaborators':
            return <span class={Css.number}>{row.collaboratorsLabel}</span>
        case 'activation':
            return <span class={Css.number}>{row.activationLabel}</span>
        case 'engagement':
            return <span class={Css.number}>{row.engagementLabel}</span>
        case 'retention':
            return <span class={Css.number}>{row.retentionLabel}</span>
        case 'training':
            return <span class={Css.number}>{row.trainingLabel}</span>
        default: {
            // Todo campo tem a sua celula: um campo novo sem `case` nao compila - e, se
            // escapar (um campo vindo de fora do tipo), a celula fica vazia, e nao com o
            // nome do campo escrito.
            const semCelula: never = field
            void semCelula
            return <span />
        }
    }
}

/** A cor de cada faixa; o TEXTO da faixa vem da API. */
const BAND_CLASSES: Record<string, string> = {
    IDEAL: Css.ideal,
    BOM: Css.good,
    EM_RISCO: Css.atRisk,
    SEM_USO: Css.noUse,
    AMOSTRA_INSUFICIENTE: Css.noSample
}

function Comparison(props: { scope: IcpScope }): JSX.Element {
    return (
        <Panel title="Comparação por segmento e por porte">
            <p class={Css.note}>
                Score médio das empresas com amostra suficiente; engajamento e retenção somados por colaborador.
            </p>

            <div class={Css.halves}>
                <GroupTable title="Por segmento" rows={props.scope.bySegment} />
                <GroupTable title="Por porte" rows={props.scope.bySize} />
            </div>
        </Panel>
    )
}

function GroupTable(props: { title: string; rows: IcpGroupRowScope[] }): JSX.Element {
    return (
        <div class={Css.half}>
            <h3 class={Css.halfTitle}>{props.title}</h3>
            <div class={Css.scroller}>
                <table class={Css.table}>
                    <thead>
                        <tr class={Css.tableHead}>
                            <th class={Css.textColumn}>Grupo</th>
                            <th class={Css.numberColumn}>Empresas</th>
                            <th class={Css.numberColumn}>Com score</th>
                            <th class={Css.numberColumn}>Score médio</th>
                            <th class={Css.numberColumn}>Engajamento</th>
                            <th class={Css.numberColumn}>Retenção</th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={props.rows}>
                            {(row) => (
                                <tr class={Css.tableRow}>
                                    <td class={Css.textCell}>{row.label}</td>
                                    <td class={Css.numberCell}>{row.companiesLabel}</td>
                                    <td class={Css.numberCell}>{row.withScoreLabel}</td>
                                    <td class={`${Css.numberCell} ${Css.strong}`}>{row.averageScoreLabel}</td>
                                    <td class={Css.numberCell}>{row.engagementLabel}</td>
                                    <td class={Css.numberCell}>{row.retentionLabel}</td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

/** Como o score sai, recolhido por padrao - o mesmo desenho da nota de Adocao e Engajamento. */
function Methodology(props: { scope: IcpScope }): JSX.Element {
    const [open, setOpen] = createSignal(false)

    return (
        <section class={Css.method}>
            <button type="button" onClick={() => setOpen(!open())} class={Css.methodTrigger}>
                <span class={Css.methodTitle}>
                    <Icon name="alert" size={16} class={Css.methodIcon} />
                    {props.scope.methodologyTitle}
                </span>
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class={Css.methodArrow}
                    classList={{ [Css.open]: open() }}
                >
                    <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>

            <ul class={Css.methodBody} classList={{ [Css.open]: open() }}>
                <For each={props.scope.methodology}>{(item) => <li>{item}</li>}</For>
            </ul>
        </section>
    )
}

// ========== PERSONA ==========

function Persona(props: { scope: IcpScope }): JSX.Element {
    return (
        <Show
            when={!props.scope.personaLoading}
            fallback={
                <div class={Css.computing}>
                    <Spinner class={Css.spinner} />
                    Apurando a persona…
                </div>
            }
        >
            <Panel title="Persona agregada">
                <p class={Css.note}>{props.scope.personaDescription}</p>

                <div class={Css.personaGrid}>
                    <For each={props.scope.personaTables}>{(table) => <PersonaTable scope={table} />}</For>
                </div>

                <p class={Css.personaFootnote}>{props.scope.personaFootnote}</p>
            </Panel>
        </Show>
    )
}

function PersonaTable(props: { scope: IcpPersonaTableScope }): JSX.Element {
    return (
        <div class={Css.half}>
            <h3 class={Css.halfTitle}>{props.scope.label}</h3>
            <div class={Css.scroller}>
                <table class={Css.table}>
                    <thead>
                        <tr class={Css.tableHead}>
                            <th class={Css.textColumn} />
                            <th class={Css.numberColumn}>B2B pessoas</th>
                            <th class={Css.numberColumn}>B2B engajamento</th>
                            <th class={Css.numberColumn}>Sem empresa</th>
                            <th class={Css.numberColumn}>Engajamento</th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={props.scope.rows}>
                            {(row) => (
                                <tr class={Css.tableRow}>
                                    <td class={Css.textCell}>{row.label}</td>
                                    <PersonaCells cell={row.b2b} />
                                    <PersonaCells cell={row.b2c} />
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>
        </div>
    )
}

/** Um lado da linha: o valor que nao existe, o grupo pequeno demais, ou os numeros. */
function PersonaCells(props: { cell: IcpPersonaCellScope }): JSX.Element {
    return (
        <Show
            when={!props.cell.absent}
            fallback={
                <>
                    <td class={`${Css.numberCell} ${Css.muted}`}>-</td>
                    <td class={`${Css.numberCell} ${Css.muted}`}>-</td>
                </>
            }
        >
            <Show
                when={!props.cell.suppressed}
                fallback={
                    <td colSpan={2} class={`${Css.numberCell} ${Css.muted}`}>
                        {props.cell.peopleLabel}
                    </td>
                }
            >
                <td class={Css.numberCell}>
                    {props.cell.peopleLabel}
                    <span class={Css.muted}> ({props.cell.shareLabel})</span>
                </td>
                <td class={`${Css.numberCell} ${Css.strong}`}>{props.cell.engagementLabel}</td>
            </Show>
        </Show>
    )
}
